import graphql__9970216dbbc2d39cf594 from "./__generated__/ContributionsMapNewContributionBox_map.graphql.js";import { useCallback, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useSelector, useDispatch } from "react-redux";
import { defineMessages } from "react-intl";

import {
  useFormatMessage,
  useFormatMessageWithCustomization
} from "../../../Intl/Context";
import { useMapContext } from "../../../Maps/MapContext";
import { useMapPanel } from "../../../Maps/MapPanel/withMapPanel";
import { useNavigateTo } from "../../../Routes/RoutesContext";
import MapElementImage from "../../../Maps/MapElementImage";
import MapElementDraggableSource, {
  SOURCE_TYPE_NEW_CONTRIBUTION
} from "../../../Maps/MapElementDraggableSource";
import ContributionsMapNewContributionDraggableElement from "./ContributionsMapNewContributionDraggableElement";
import Button from "../../../Buttons/Button";
import FeatureTooltip from "../../../Tooltip/FeatureTooltip";
import { Desktop, NonDesktop } from "../../../Utils/ResponsiveQueries";

import { closeMapPanel } from "../../../../reducers/map/panels";
import {
  addMapContribution,
  setMapDraggedContribution,
  unsetMapDraggedContribution,
  enableMapContributionCreation
} from "../../../../reducers/map/contribution";
import { useDeferredEffect } from "../../../Utils/useDeferredEffect";

const ContributionsMapNewContributionBox = props => {
  const map = useFragment(ContributionsMapNewContributionBox_map, props.map);

  const { newContributionMarkerStyle, participativeModule } = map;
  const { slug: participativeModuleSlug } = participativeModule;

  const { mapKey } = useMapContext();
  const { id: panelId } = useMapPanel();

  const { draggedContribution: isContributionDragged } = useSelector(
    ({ map }) => map[mapKey].contribution
  );

  const t = useFormatMessage();
  const mobileHint = t(intlMessages.mobileHint);
  const tooltipDescription = t(intlMessages.tooltipDescription);
  const dropErrorMessage = t(intlMessages.dropErrorMessage);

  const translateWithCustomization = useFormatMessageWithCustomization(
    participativeModule.visualization.customIntlMessages
  );
  const title = translateWithCustomization(intlMessages.title);
  const manualContribution = translateWithCustomization(intlMessages.manualContribution);

  const dismissTooltipRef = useRef();
  const dispatch = useDispatch();

  const handleContributionDragStart = useCallback(() => {
    dispatch(setMapDraggedContribution(mapKey));
  }, [dispatch, mapKey]);

  const handleContributionDragEnd = useCallback(() => {
    // The setTimeout avoids tooltip flashes when the element is dropped,
    // because the state seems to be set before the onMouseLeave callback is
    // fired
    setTimeout(() => dispatch(unsetMapDraggedContribution(mapKey)), 10);
  }, [dispatch, mapKey]);

  useDeferredEffect(() => {
    if (isContributionDragged && dismissTooltipRef.current) dismissTooltipRef.current();
  }, [isContributionDragged]);

  const navigateTo = useNavigateTo();

  const handleContributionDropped = useCallback(
    dropResult => {
      dispatch(addMapContribution(mapKey, { ...dropResult, explicitlyGeolocated: true }));
      navigateTo("newContribution", { participativeModuleSlug });
    },
    [dispatch, mapKey, navigateTo, participativeModuleSlug]
  );

  const addManualContribution = useCallback(() => {
    if (dismissTooltipRef.current) dismissTooltipRef.current();

    dispatch(addMapContribution(mapKey, { explicitlyGeolocated: false }));

    navigateTo("newContribution", {
      participativeModuleSlug
    });
  }, [dispatch, mapKey, navigateTo, participativeModuleSlug]);

  const handleMobileLocatableContributionButtonClicked = useCallback(() => {
    dispatch(enableMapContributionCreation(mapKey));
    dispatch(closeMapPanel(mapKey, panelId));
  }, [dispatch, mapKey, panelId]);

  return (
    <>
      <div className="box">
        <div className="items-group padding-sm bg-primary">
          <NonDesktop>
            <div className="items-group-main d-flex justify-content-center flex-column margin-right-xs">
              <div className="text-subtitle-page">{title}</div>
              <div className="text-default margin-top-xs">{mobileHint}</div>
            </div>

            <Button
              type="button"
              onClick={handleMobileLocatableContributionButtonClicked}
              className="btn btn-primary-inverse btn-shadow padding-sm"
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: MapElementImage({
                    ...newContributionMarkerStyle,
                    position: "static"
                  })
                }}
              />
            </Button>
          </NonDesktop>

          <Desktop>
            <FeatureTooltip
              featureName="contributionsMapNewContributionBoxDragging"
              placement="right"
              keepAfterDismissed={true}
              message={tooltipDescription}
              clickable={true}
              dismissTooltipRef={dismissTooltipRef}
              appearFrom="right"
            >
              <div className="items-group-main d-flex align-items-center">
                <div className="text-subtitle-page flex-1 font-weight-bold">{title}</div>

                <div className="items-group-addon padding-left-sm d-flex align-items-center">
                  <div className="padding-xs map-element-contribution-draggable-source-container border-radius bg-primary-inverse">
                    <MapElementDraggableSource
                      onDragStart={handleContributionDragStart}
                      onDragEnd={handleContributionDragEnd}
                      onDropped={handleContributionDropped}
                      sourceItem={{
                        type: SOURCE_TYPE_NEW_CONTRIBUTION,
                        style: newContributionMarkerStyle
                      }}
                      dropErrorMessage={dropErrorMessage}
                    >
                      {dragPreviewRef => (
                        <ContributionsMapNewContributionDraggableElement
                          style={newContributionMarkerStyle}
                          dragPreviewRef={dragPreviewRef}
                        />
                      )}
                    </MapElementDraggableSource>
                  </div>
                </div>
              </div>
            </FeatureTooltip>
          </Desktop>
        </div>

        <Button
          type="button"
          onClick={addManualContribution}
          className="btn-unstyled btn-block text-muted text-left text-small padding-sm box-shadow"
          label={manualContribution}
          iconClassName="icon-arrow-right d-inline margin-left-xxs"
          iconPosition="after"
          data-contribute-without-location-button
        />
      </div>
    </>
  );
};

const ContributionsMapNewContributionBox_map = graphql__9970216dbbc2d39cf594;

export default ContributionsMapNewContributionBox;

const intlMessages = defineMessages({
  title: {
    id: "map.debate.contribution.panel.title",
    defaultMessage: "Ajouter une contribution",
    description: `
      Titre du libellé d'ajout de contribution en glisser-déposer sur la carte
    `
  },
  mobileHint: {
    id: "map.debate.contribution.panel.mobileHint",
    defaultMessage: `
      Cliquez sur le bouton ci-contre pour ajouter votre contribution sur la carte
    `
  },
  manualContribution: {
    id: "map.debate.contribution.panel.manualContribution",
    defaultMessage: "Ajouter une contribution sans glisser l'icône",
    description: "Libellé du bouton d'ajout de contribution manuelle sur la carte"
  },
  tooltipDescription: {
    id: "map.debate.contribution.panel.tooltipDescription",
    defaultMessage: `
      Afin de localiser votre contribution, glissez l’icône ci-contre sur la
      carte et déposez là dans la zone de contribution qui apparaît en couleur.
    `
  },
  dropErrorMessage: {
    id: "map.debate.contribution.panel.dropErrorMessage",
    defaultMessage: `
      Merci de déposer votre contribution dans la zone de contribution qui
      apparaît en couleur lorsque vous glissez l'icône.
    `
  }
});
